import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  ShoppingCart,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  CheckCircle,
  Clock,
  Package,
  XCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Filter,
  X,
  Download,
  FileText,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';
import { usePermission } from '@/components/guards/permission-guard';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { useSessionState } from '@/hooks/use-session-state';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { dateStamp } from '@/lib/download-xlsx';
import { buildPurchaseOrdersDoc } from '@/lib/purchase-orders-export';
import { downloadTableReportPdf } from '@/lib/report-table-pdf';
import { exportTableReportXlsx } from '@/lib/report-table-xlsx';

type SortField =
  | 'orderNumber'
  | 'orderDate'
  | 'total'
  | 'status'
  | 'supplier'
  | 'warehouse'
  | 'currency';
type SortOrder = 'asc' | 'desc';

const statusConfig: Record<string, { color: string; icon: any }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  PENDING_APPROVAL: { color: 'bg-amber-100 text-amber-700', icon: Clock },
  APPROVED: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  PARTIALLY_RECEIVED: { color: 'bg-purple-100 text-purple-700', icon: Package },
  FULLY_RECEIVED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CANCELLED: { color: 'bg-red-100 text-red-700', icon: XCircle },
};

const STATUSES = [
  'PENDING_APPROVAL',
  'APPROVED',
  'PARTIALLY_RECEIVED',
  'FULLY_RECEIVED',
  'CANCELLED',
] as const;

export function PurchaseOrdersPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canCreate = usePermission('PURCHASE_ORDERS', 'CREATE');
  const canEdit = usePermission('PURCHASE_ORDERS', 'EDIT');
  const canDelete = usePermission('PURCHASE_ORDERS', 'DELETE');

  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:purchasing:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedStatus, setSelectedStatus] = useSessionState<string>(
    'trading:purchasing:selectedStatus',
    'all'
  );
  const [selectedSupplier, setSelectedSupplier] = useSessionState<string>(
    'trading:purchasing:selectedSupplier',
    'all'
  );
  const [selectedWarehouse, setSelectedWarehouse] = useSessionState<string>(
    'trading:purchasing:selectedWarehouse',
    'all'
  );
  const [selectedCurrency, setSelectedCurrency] = useSessionState<string>(
    'trading:purchasing:selectedCurrency',
    'all'
  );
  const [sortBy, setSortBy] = useSessionState<SortField | undefined>(
    'trading:purchasing:sortBy',
    undefined
  );
  const [sortOrder, setSortOrder] = useSessionState<SortOrder>(
    'trading:purchasing:sortOrder',
    'desc'
  );
  const [page, setPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'orderDate' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const handleFilter = (type: 'status' | 'supplier' | 'warehouse' | 'currency', value: string) => {
    switch (type) {
      case 'status':
        setSelectedStatus(value);
        break;
      case 'supplier':
        setSelectedSupplier(value);
        break;
      case 'warehouse':
        setSelectedWarehouse(value);
        break;
      case 'currency':
        setSelectedCurrency(value);
        break;
    }
    setPage(1);
  };

  const activeFilterCount = [
    selectedStatus,
    selectedSupplier,
    selectedWarehouse,
    selectedCurrency,
  ].filter((v) => v !== 'all').length;

  const clearAllFilters = () => {
    setSelectedStatus('all');
    setSelectedSupplier('all');
    setSelectedWarehouse('all');
    setSelectedCurrency('all');
    setSearchQuery('');
    setDebouncedSearch('');
    setPage(1);
  };

  // Queries
  const { data: suppliersData } = trpc.tradingSupplier.list.useQuery({});
  const { data: warehousesData } = trpc.warehouse.list.useQuery({});

  const { data, isLoading, error } = trpc.tradingPurchaseOrder.list.useQuery({
    search: debouncedSearch || undefined,
    status: selectedStatus !== 'all' ? (selectedStatus as (typeof STATUSES)[number]) : undefined,
    supplierId: selectedSupplier !== 'all' ? selectedSupplier : undefined,
    warehouseId: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
    currency: selectedCurrency !== 'all' ? selectedCurrency : undefined,
    sortBy,
    sortOrder,
    page,
  });

  const utils = trpc.useUtils();

  const runExport = async (kind: 'pdf' | 'xlsx') => {
    setExporting(true);
    try {
      const paramsSummary: string[] = [];
      if (debouncedSearch) paramsSummary.push(`Arama: ${debouncedSearch}`);
      if (selectedStatus !== 'all') paramsSummary.push(`Durum: ${getStatusLabel(selectedStatus)}`);
      if (selectedSupplier !== 'all') {
        const name = suppliers.find((s) => s.id === selectedSupplier)?.companyName;
        if (name) paramsSummary.push(`Tedarikçi: ${name}`);
      }
      if (selectedWarehouse !== 'all') {
        const name = warehouseOptions.find((w) => w.id === selectedWarehouse)?.name;
        if (name) paramsSummary.push(`Depo: ${name}`);
      }
      if (selectedCurrency !== 'all') paramsSummary.push(`Para Birimi: ${selectedCurrency}`);

      const data = await utils.tradingPurchaseOrder.exportList.fetch({
        search: debouncedSearch || undefined,
        status:
          selectedStatus !== 'all' ? (selectedStatus as (typeof STATUSES)[number]) : undefined,
        supplierId: selectedSupplier !== 'all' ? selectedSupplier : undefined,
        warehouseId: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
        currency: selectedCurrency !== 'all' ? selectedCurrency : undefined,
      });

      const doc = buildPurchaseOrdersDoc(data.rows, paramsSummary);
      const fn = `trading-satinalma-siparisleri-${dateStamp()}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      if (kind === 'pdf') {
        await downloadTableReportPdf(doc, fn);
      } else {
        await exportTableReportXlsx(doc, fn);
      }
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Dışa aktarma başarısız');
    } finally {
      setExporting(false);
    }
  };

  const deleteMutation = trpc.tradingPurchaseOrder.delete.useMutation({
    onSuccess: () => {
      utils.tradingPurchaseOrder.list.invalidate();
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setOrderToDelete(null);
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const suppliers = suppliersData?.suppliers ?? [];
  const warehouses = warehousesData ?? [];
  const orders = data?.orders ?? [];
  const pagination = data?.pagination;

  // Derive unique currencies from current data (all pages won't be available, but this is good enough)
  const currencies = useMemo(() => {
    const set = new Set(orders.map((o) => o.currency));
    return Array.from(set).sort();
  }, [orders]);

  // Derive unique warehouses from orders
  const warehouseOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehouses) {
      map.set(w.id, w.name);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [warehouses]);

  const handleDeleteOrder = (id: string) => {
    setOrderToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (orderToDelete) {
      deleteMutation.mutate(orderToDelete);
    }
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      PENDING_APPROVAL: t('purchasing.purchaseOrders.pendingApproval'),
      APPROVED: t('purchasing.purchaseOrders.approved'),
      PARTIALLY_RECEIVED: t('purchasing.purchaseOrders.partiallyReceived'),
      FULLY_RECEIVED: t('purchasing.purchaseOrders.fullyReceived'),
      CANCELLED: t('purchasing.purchaseOrders.cancelled'),
    };
    return map[status] ?? status;
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Sortable + filterable column header
  const SortHeader = ({
    label,
    field,
    align = 'left',
    filterType,
    filterOptions,
    filterValue,
  }: {
    label: string;
    field?: SortField;
    align?: 'left' | 'center' | 'right';
    filterType?: 'status' | 'supplier' | 'warehouse' | 'currency';
    filterOptions?: { value: string; label: string }[];
    filterValue?: string;
  }) => {
    const isActive = field && sortBy === field;
    const isFiltered = filterValue && filterValue !== 'all';
    const alignClass =
      align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

    return (
      <th className="px-3 py-2 text-sm font-medium">
        <div className={`flex items-center gap-1 ${alignClass}`}>
          {field ? (
            <button
              type="button"
              onClick={() => handleSort(field)}
              className={`hover:text-foreground inline-flex items-center gap-1 transition-colors ${
                isActive ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              {label}
              {isActive ? (
                sortOrder === 'asc' ? (
                  <ArrowUp className="h-3 w-3" />
                ) : (
                  <ArrowDown className="h-3 w-3" />
                )
              ) : (
                <ArrowUpDown className="h-3 w-3 opacity-40" />
              )}
            </button>
          ) : (
            <span className="text-muted-foreground">{label}</span>
          )}
          {filterType && filterOptions && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`hover:bg-muted rounded p-0.5 transition-colors ${
                    isFiltered
                      ? 'text-primary'
                      : 'text-muted-foreground/50 hover:text-muted-foreground'
                  }`}
                >
                  <Filter className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" align="start">
                <button
                  type="button"
                  onClick={() => handleFilter(filterType, 'all')}
                  className={`hover:bg-muted w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${
                    !isFiltered ? 'text-foreground font-medium' : 'text-muted-foreground'
                  }`}
                >
                  {tCommon('all')}
                </button>
                {filterOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleFilter(filterType, opt.value)}
                    className={`hover:bg-muted w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${
                      filterValue === opt.value ? 'text-foreground bg-muted/50 font-medium' : ''
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('purchasing.purchaseOrders.title')}</h1>
          <p className="text-muted-foreground">{t('purchasing.purchaseOrders.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting}>
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {t('common.export', 'Dışa Aktar')}
                <ChevronDown className="ml-2 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => runExport('pdf')}>
                <FileText className="mr-2 h-4 w-4 text-red-600" />
                {t('common.exportPdf', 'PDF')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => runExport('xlsx')}>
                <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                {t('common.exportExcel', 'Excel')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canCreate && (
            <Button onClick={() => navigate('/trading/purchasing/new')}>
              <Plus className="mr-2 h-4 w-4" />
              {t('purchasing.purchaseOrders.addOrder')}
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('purchasing.purchaseOrders.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Search + active filter chips */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('purchasing.purchaseOrders.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <>
            {selectedStatus !== 'all' && (
              <span className="bg-muted border-border inline-flex items-center gap-1 rounded border px-2 py-1 text-xs">
                {getStatusLabel(selectedStatus)}
                <button
                  type="button"
                  onClick={() => handleFilter('status', 'all')}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedSupplier !== 'all' && (
              <span className="bg-muted border-border inline-flex items-center gap-1 rounded border px-2 py-1 text-xs">
                {suppliers.find((s) => s.id === selectedSupplier)?.companyName}
                <button
                  type="button"
                  onClick={() => handleFilter('supplier', 'all')}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedWarehouse !== 'all' && (
              <span className="bg-muted border-border inline-flex items-center gap-1 rounded border px-2 py-1 text-xs">
                {warehouseOptions.find((w) => w.id === selectedWarehouse)?.name}
                <button
                  type="button"
                  onClick={() => handleFilter('warehouse', 'all')}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedCurrency !== 'all' && (
              <span className="bg-muted border-border inline-flex items-center gap-1 rounded border px-2 py-1 text-xs">
                {selectedCurrency}
                <button
                  type="button"
                  onClick={() => handleFilter('currency', 'all')}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-muted-foreground hover:text-foreground text-xs underline"
            >
              {tCommon('clearAll')}
            </button>
          </>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && orders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ShoppingCart className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('purchasing.purchaseOrders.noOrders')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('purchasing.purchaseOrders.noOrdersDesc')}
          </p>
          {canCreate && (
            <Button className="mt-4" onClick={() => navigate('/trading/purchasing/new')}>
              <Plus className="mr-2 h-4 w-4" />
              {t('purchasing.purchaseOrders.addOrder')}
            </Button>
          )}
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && orders.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <SortHeader
                    label={t('purchasing.purchaseOrders.orderNumber')}
                    field="orderNumber"
                  />
                  <th className="text-muted-foreground px-3 py-2 text-left text-sm font-medium">
                    {t('purchasing.purchaseOrders.invoiceNumber')}
                  </th>
                  <SortHeader
                    label={t('purchasing.purchaseOrders.supplier')}
                    field="supplier"
                    filterType="supplier"
                    filterValue={selectedSupplier}
                    filterOptions={suppliers.map((s) => ({ value: s.id, label: s.companyName }))}
                  />
                  <SortHeader
                    label={t('purchasing.purchaseOrders.warehouse')}
                    field="warehouse"
                    filterType="warehouse"
                    filterValue={selectedWarehouse}
                    filterOptions={warehouseOptions.map((w) => ({ value: w.id, label: w.name }))}
                  />
                  <SortHeader
                    label={t('purchasing.purchaseOrders.status')}
                    field="status"
                    align="center"
                    filterType="status"
                    filterValue={selectedStatus}
                    filterOptions={STATUSES.map((s) => ({ value: s, label: getStatusLabel(s) }))}
                  />
                  <SortHeader label={t('purchasing.purchaseOrders.orderDate')} field="orderDate" />
                  <th className="text-muted-foreground px-3 py-2 text-right text-sm font-medium">
                    {t('purchasing.purchaseOrders.items')}
                  </th>
                  <SortHeader
                    label={t('purchasing.purchaseOrders.total')}
                    field="total"
                    align="right"
                    filterType="currency"
                    filterValue={selectedCurrency}
                    filterOptions={currencies.map((c) => ({ value: c, label: c }))}
                  />
                  <th className="text-muted-foreground px-3 py-2 text-center text-sm font-medium">
                    {tCommon('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.map((order) => {
                  const config = statusConfig[order.status] || statusConfig.DRAFT;
                  const StatusIcon = config?.icon ?? Clock;

                  return (
                    <tr key={order.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2.5">
                        <Link
                          to={`/trading/purchasing/${order.id}`}
                          className="text-primary font-mono text-sm font-medium hover:underline"
                        >
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-sm">
                        {order.invoiceNumber || '-'}
                      </td>
                      <td className="px-3 py-2.5 text-sm">{order.supplierName}</td>
                      <td className="px-3 py-2.5 text-sm">{order.warehouseName}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-sm">{formatDate(order.orderDate)}</td>
                      <td className="px-3 py-2.5 text-right text-sm">{order.itemCount}</td>
                      <td className="px-3 py-2.5 text-right text-sm font-medium">
                        {formatCurrency(order.total, order.currency)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/trading/purchasing/${order.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            {canEdit && (
                              <DropdownMenuItem
                                onClick={() => navigate(`/trading/purchasing/${order.id}/edit`)}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                {tCommon('edit')}
                              </DropdownMenuItem>
                            )}
                            {canEdit &&
                              (order.status === 'APPROVED' ||
                                order.status === 'PARTIALLY_RECEIVED') && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/trading/purchasing/receipts/new?poId=${order.id}`)
                                  }
                                >
                                  <Package className="mr-2 h-4 w-4" />
                                  {t('purchasing.purchaseOrders.receive')}
                                </DropdownMenuItem>
                              )}
                            {canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteOrder(order.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {tCommon('delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border bg-background hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border bg-background hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
